/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    string, bool, arrayOf,
    shape, func,
} from 'prop-types';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

// Material UI
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';

// Helpers
import { getCryptedOrderDetailsURL } from '../../utils/crypto';
import configENV from '../../config';
import { getFeatureFlags } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { isEmpty } from '../../utils/object';
import { secureProtocol } from '../../utils/secureProtocol';
import brandNames from '../../assets/data/BrandNames';
import Helper from '../../state/reducers/Helper';

// Components
import StatusTracking from '../../common/StatusTracking';

const {
    FEDEX_ORDERTRACKING_URL,
    UPS_ORDERTRACKING_URL,
    USPS_ORDERTRACKING_URL,
    UDS_ORDERTRACKING_URL,
} = configENV;

const OrderListItem = ({
    className, userProfile, ROSSOrder, showTrackingWidgetForSameLogicalItems,
}) => {
    const history = useHistory();

    const featureFlags = useSelector((state) => getFeatureFlags(state));

    const IsPendingCancelEnabled = featureFlags?.['is-pending-cancel-enabled'];
    const isHidingCardIsleImage = featureFlags['is-hiding-cardisle-image'];

    const handleViewDetailsClicked = (
        uuid,
        orderNumber,
        isLegacyVCOrder,
    ) => {
        if (orderNumber && uuid) {
            // if logged in user clicked on the view details button, redirect to the order detail page
            const ordersURL = getCryptedOrderDetailsURL(orderNumber, uuid, null, null, isLegacyVCOrder);
            history.push(ordersURL);
        }
    };

    const renderTrackingLink = (carrier, trackingNumber, deliveryLink = '', productBrandCode = '') => {
        if (productBrandCode === '1036') {
            return trackingNumber;
        }

        let carrierUrl;
        switch (carrier) {
            case 'FEDEX':
                carrierUrl = FEDEX_ORDERTRACKING_URL;
                break;
            case 'UPS':
                carrierUrl = UPS_ORDERTRACKING_URL;
                break;
            case 'USPS':
            case 'MAIL':
                carrierUrl = USPS_ORDERTRACKING_URL;
                break;
            case 'UDS':
                carrierUrl = UDS_ORDERTRACKING_URL;
                break;
            default:
                carrierUrl = null;
        }
        if (!deliveryLink) {
            return carrierUrl ? (
                <a
                    href={carrierUrl + trackingNumber}
                    target="_blank"
                    rel="noreferrer"
                    className="link"
                >
                    {trackingNumber}
                </a>
            ) : (
                <span>{trackingNumber}</span>
            );
        }
        return (
            <a
                href={deliveryLink}
                target="_blank"
                rel="noreferrer"
                className="link"
            >
                {trackingNumber}
            </a>
        );
    };

    const showSubStatus = (ROSSItem = {}) => {
        const {
            statusDescription, history, type, deliveryLink,
        } = ROSSItem;
        if (type === 'FPT') {
            const isOrderDelayed = history.length > 0 && history[0].attemptedReason;
            if (isOrderDelayed) {
                const deliveryDescription = statusDescription || history[0].statusDescription;
                return (
                    <div className="delay-container">
                        <img src="https://images.contentstack.io/v3/assets/blt95e0138311135214/blte21ce9e1faee603c/6042a5473c41f30bce48321a/Warning_Icon.svg" alt="warning" />
                        <b>{deliveryDescription}</b>
                    </div>
                );
            }
            // For mobile only, replaced `Delivered on June 11, 2021 at 01:00 PM` ==> `Delivered on June 11, 2021`
            const statusDescriptionMobile = statusDescription ? statusDescription.replace(/^(Delivered on[\s\S]+)(\sat\s\d\d:\d\d\s[AP]M)$/, '$1') : statusDescription;
            return <b>{statusDescriptionMobile}</b>;
        }

        if (type === 'GPT') {
            const isOrderDelayed = history.length > 0 && history[0].codeStatus === 'DE';
            if (isOrderDelayed) {
                const deliveryDescription = statusDescription || history[0].statusDescription;
                return (
                    <div className="delay-container">
                        <img src="https://images.contentstack.io/v3/assets/blt95e0138311135214/blte21ce9e1faee603c/6042a5473c41f30bce48321a/Warning_Icon.svg" alt="warning" />
                        { deliveryLink ? (
                            <a
                                href={deliveryLink}
                                target="_blank"
                                rel="noreferrer"
                                className="link"
                            >
                                {deliveryDescription}
                            </a>
                        ) : <b>{deliveryDescription}</b>}
                    </div>
                );
            }
            return deliveryLink ? (
                <a
                    href={deliveryLink}
                    target="_blank"
                    rel="noreferrer"
                    className="link"
                >
                    {statusDescription}
                </a>
            ) : <b>{statusDescription}</b>;
        }
        return statusDescription ? (
            <b>{statusDescription}</b>
        ) : null;
    };

    const showCarrier = (ROSSCarrier) => {
        if (ROSSCarrier === 'FEDEX') {
            return <img alt="fedex logo" src="https://images.contentstack.io/v3/assets/blt95e0138311135214/blt986d6df1eca50df5/5f9715bf545bdb56ce4903e6/fedex-logo.svg" />;
        }
        if (ROSSCarrier === 'UDS') {
            return <img alt="uds logo" src="https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/bltc6883e40544a7af1/6165e3b4a481550b84bdc2eb/UDS_logo_1.svg" />;
        }
        return <span className="bold">{ROSSCarrier}</span>;
    };

    const subscriptionInterval = (i) => {
        const SUBSCRIPTION_INTERVALS = {
            1: 'Delivers every 1 week',
            2: 'Delivers every 2 weeks',
            4: 'Delivers every 4 weeks',
            6: 'Delivers every 6 weeks',
            8: 'Delivers every 8 weeks',
            30: 'Delivers monthly',
            60: 'Delivers every other month',
        };
        return SUBSCRIPTION_INTERVALS[i] ? SUBSCRIPTION_INTERVALS[i] : i;
    };

    // Manage Subscriptions
    const showMSButton = (ROSSItem) => (
        <>
            {ROSSItem.interval ? (
                <div className="subscription">
                    <div className="subscriptionDelivery">{subscriptionInterval(ROSSItem.interval)}</div>
                    <Link to="/account/subscriptions">Manage Subscriptions</Link>
                </div>
            ) : ''}
        </>
    );

    if (!userProfile) { return null; }

    return !isEmpty(ROSSOrder) && (
        <div className={`orderListItem ${className} ${userProfile?.isAuthenticated ? '' : 'negative-margin'}`} style={{ padding: 0, borderTop: 0 }}>
            <div className="orderListHeader">
                <div>Order #: {ROSSOrder.orderNumber}</div>
                <Button
                    variant="contained"
                    className="mobileCtaButton"
                    onClick={() => handleViewDetailsClicked(
                        ROSSOrder.uuid,
                        ROSSOrder.orderNumber,
                        ROSSOrder.vtcorder,
                    )}
                >
                    {ROSSOrder.modifyOrder === 'true'
                        ? 'View Details / Modify'
                        : 'View Details'}
                </Button>
            </div>
            {ROSSOrder.items.map((ROSSItem, i) => {
                const passportRegex = /(passport annual membership|passport for|passport at)/gi;
                const passPortLogo = 'https://images.contentstack.io/v3/assets/blt95e0138311135214/bltd8ac364c488b6313/5fd24cc550460c30dd5404bc/passport-logo.jpg';
                const fallbackImgURL = 'https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/bltc881f4b32ef5f484/60dcd2de4b10c259bbae6426/Image_Not_Available.png';
                let ROSSProductImageURL = ROSSItem?.prdImgCtg && ROSSItem?.prdImgCtg !== '' ? secureProtocol(ROSSItem?.prdImgCtg) : fallbackImgURL;
                const ROSSOrderPendingCancel = ROSSItem?.orderStatusAttributes?.pendingCancel;
                const ROSSDeliveryStatus = ROSSItem?.status;
                const isPassportItem = ROSSItem.subscriptionType === 'Passport' || ROSSItem?.productName?.match(passportRegex)?.length > 0;
                const ROSSRecip = `${ROSSItem?.recpFirstName} ${ROSSItem?.recpLastName}`;
                const isSameLogicalOrder = ROSSOrder?.intOrderNumber === ROSSItem?.intOrderNumber;
                const isCYOColChild = ROSSItem?.addonType === 'CYO' || ROSSItem?.addonType === 'COLLECTION';
                const isAddon = ROSSItem.addonType === 'ADDON';
                const isDonationItem = Helper.checkIsDonationItem(ROSSItem?.productName);

                // bandaid fix for hiding card isle image if order is delivered
                if (isHidingCardIsleImage && Helper.isDeliveredCardIsle(ROSSItem)) {
                    ROSSProductImageURL = fallbackImgURL;
                }

                if (!ROSSItem?.displayed) return null;

                return (
                    <React.Fragment key={['_', i].join('')}>
                        {(!isCYOColChild && (
                            <Grid container className="orderListProduct">
                                <Grid className="orderInfo" item xs={9}>
                                    {(i === 0 || ((!isSameLogicalOrder && !isAddon) || showTrackingWidgetForSameLogicalItems(ROSSOrder, ROSSItem))) && (
                                        <>
                                            <div className="recipientInfo">
                                                <div className="bold recipientName">
                                                    {ROSSRecip}
                                                </div>
                                                <div className="small uppercase">
                                                    {ROSSItem?.businessName
                                                        ? ROSSItem?.businessName
                                                        : ''}
                                                </div>
                                            </div>
                                            {
                                                isPassportItem ? (
                                                    <div className="orderDetail">
                                                        <Link to="/account/passport" className="display-flex left">
                                                            View Your Passport Details
                                                        </Link>
                                                    </div>
                                                ) : (
                                                    <StatusTracking
                                                        ROSSStatus={isDonationItem ? 'DELIVERED' : ROSSDeliveryStatus}
                                                        ROSSOrderPendingCancel={ROSSOrderPendingCancel}
                                                        IsPendingCancelEnabled={IsPendingCancelEnabled}
                                                    />
                                                )
                                            }
                                            <div className="subStatusContainer">
                                                {showSubStatus(ROSSItem?.orderStatusAttributes)}
                                            </div>
                                            {/* Display order status information if it's not the cancelled, pending cancel and donation item */}
                                            {ROSSDeliveryStatus !== 'CANCELLED' && !ROSSOrderPendingCancel && !isDonationItem && !isPassportItem
                                                ? (
                                                    <>
                                                        {ROSSItem.itemType === 'FPT'
                                                            ? (
                                                                <>
                                                                    <Grid className="orderDetail orderDetailMargin" item xs={12}>
                                                                        <span className="gray-color uppercase">Delivered by: </span>
                                                                        <span className="bold">Local Shop</span>
                                                                    </Grid>
                                                                    {ROSSItem.dtwName
                                                                        ? (
                                                                            <Grid className="orderDetail" item xs={12}>
                                                                                <span className="gray-color uppercase">Requested time: </span>
                                                                                <span className="bold">{ROSSItem.dtwName}</span>
                                                                            </Grid>
                                                                        ) : null}
                                                                </>
                                                            ) : null}
                                                        {ROSSItem.itemType === 'GPT'
                                                            ? (
                                                                <>
                                                                    {ROSSItem?.carrier && (
                                                                        <Grid className="orderDetail orderDetailMargin display-flex" item xs={12}>
                                                                            <span className="gray-color uppercase">Delivered by:&nbsp;</span>
                                                                            {showCarrier(ROSSItem?.carrier)}
                                                                        </Grid>
                                                                    )}
                                                                    {/* Won't display the Tracking # when it's delivered on mobile */}
                                                                    {(ROSSDeliveryStatus !== 'DELIVERED') && (
                                                                        <Grid className="orderDetail orderDetailMargin" item xs={12}>
                                                                            <span className="gray-color uppercase">Tracking: </span>
                                                                            <span className="bold">{ROSSItem?.trackingNumber
                                                                                ? (
                                                                                    renderTrackingLink(ROSSItem?.carrier, ROSSItem?.trackingNumber, ROSSItem?.orderStatusAttributes?.deliveryLink, ROSSItem?.productBrand)
                                                                                ) : 'Available once shipped'}
                                                                            </span>
                                                                        </Grid>
                                                                    )}
                                                                </>
                                                            ) : null}
                                                        <>
                                                            <Grid className="orderDetail" item xs={12}>
                                                                <span className="gray-color uppercase">BRAND: </span>
                                                                <span className="bold">{brandNames[ROSSItem?.productBrand]}</span>
                                                            </Grid>
                                                            {showMSButton(ROSSItem)}
                                                        </>
                                                    </>
                                                ) : null}
                                        </>
                                    )}
                                </Grid>
                                <Grid className="productImg" item xs={3}>
                                    <img
                                        src={isPassportItem ? passPortLogo : ROSSProductImageURL}
                                        alt={ROSSItem.productSku}
                                        onError={(e) => {
                                            if (e.target.src !== fallbackImgURL) {
                                                e.target.src = fallbackImgURL;
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        ))}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

OrderListItem.defaultProps = {
    className: '',
    ROSSOrder: {},
    showTrackingWidgetForSameLogicalItems: () => { },
};

OrderListItem.propTypes = {
    showTrackingWidgetForSameLogicalItems: func,
    className: string,
    userProfile: shape({
        isAuthenticated: bool.isRequired,
    }).isRequired,
    ROSSOrder: shape({
        orderNumber: string,
        modifyOrder: string,
        uuid: string,
        items: arrayOf(
            shape({
                itemType: string,
                prdImgCtg: string,
                productName: string,
                productSku: string,
                subscriptionType: string,
                paymentStatus: string,
                trackingNumber: string,
                carrier: string,
            }),
        ),
    }),
};

export default (OrderListItem);
